/* Upload de imagem do cliente: capa do evento (page.evento) e foto de perfil
 * (page.conta). Carrega SO nessas duas paginas, com defer, e por isso NAO entra
 * no core de 16KB; teto proprio de 3KB em scripts/check-js-budget.mjs.
 *
 * Este arquivo e biblioteca, nao tela: ele nao sabe uma frase, nao le locale e
 * nao toca DOM de nenhuma das duas paginas. Quem pinta preview, progresso e
 * erro e o mw-evento.js / mw-conta.js, que ja tem o i18n da propria pagina. O
 * contrato e `window.MWMidia`:
 *
 *   MWMidia.ligar(input, {
 *     url, lado,                 // rota do POST e lado do quadrado em px
 *     antes(),                   // o envio vai comecar (veu, progresso zerado)
 *     previa(objectURL),         // chamado ANTES da rede, com o JPEG ja reduzido
 *     andamento(fracao),         // 0..1 durante o envio (XHR; fetch nao da isso)
 *     ok(json), erro({ c, maxMb })
 *   })
 *   MWMidia.remover(url) -> Promise<json>, rejeitando com { c, maxMb }
 *   MWMidia.editar(input) -> Promise: abre o editor de recorte na capa salva
 *
 * CAPA COM RECORTE (R27): o input da capa leva data-recorte="3" e
 * data-recorte-js (a URL do assets/mw-recorte.js). Com eles, o `change` NAO
 * envia: carrega o editor sob demanda e entrega a ele o arquivo, as opcoes do
 * `ligar` e `{ reduzir, subir, remover }` (injecao de dependencia: o editor nao
 * duplica a reducao, o envio nem o DELETE, e este arquivo continua sem saber
 * uma frase). Input SEM data-recorte (a foto de perfil da conta) faz
 * exatamente o de antes: antes(), reduz ao quadrado, previa e envio.
 *
 * Codigos de erro (`c`): 'tamanho' (413, com maxMb do corpo), 'tipo' (415 ou o
 * navegador nao decodificou o arquivo), 'teto' (429), 'rede' (o resto).
 *
 * Por que reduzir no cliente: o servidor recusa acima de `eventosUploadMaxMb`
 * (2 MB por padrao) e foto de celular passa disso com folga. Reduzir aqui
 * transforma um 413 garantido num envio que cabe, e ainda corta o que sobe pelo
 * 4G de quem esta na festa.
 *
 * HEIC/HEIF (o formato da camera da Apple): o servidor nao aceita, porque o
 * sharp prebuilt nao decodifica. Onde o navegador decodifica (Safari), o
 * canvas reencoda em JPEG e o arquivo passa; onde nao decodifica, tanto o
 * createImageBitmap quanto o <img> falham e os dois caminhos caem no MESMO
 * codigo 'tipo' - a tela diz que o formato nao abre, em vez de mandar 4 MB de
 * HEIC para levar 415 do outro lado.
 */
(function () {
  'use strict';
  var W = window;
  if (W.MWMidia) return;

  /* JPEG 0,85: o mesmo ponto que o servidor usa no re-encode. Subir a qualidade
     aqui so engordaria o que o sharp vai recomprimir do outro lado. */
  var QUALIDADE = 0.85;
  var ultimaPrevia = null, editor = null;

  function corpoJson(texto) {
    try { return JSON.parse(texto); } catch (e) { return {}; }
  }

  /* Status HTTP -> codigo de erro da tela. O catalogo e o das rotas de midia
     (painel/src/app/api/curador/.../capa e .../perfil/foto): 413 com { maxMb },
     415 tipo, 429 teto do dia, 503/400 e o resto caem em 'rede'. */
  function erroDe(status, j) {
    if (status === 413) return { c: 'tamanho', maxMb: (j && j.maxMb) || 0 };
    if (status === 415) return { c: 'tipo' };
    if (status === 429) return { c: 'teto' };
    return { c: 'rede' };
  }

  /* Tamanho do arquivo em texto curto, para o erro dizer o numero REAL ("a imagem
     tem 9 MB") em vez de so repetir o limite. Quem escreve a frase e a tela; aqui
     so sai o numero formatado, porque este arquivo nao le locale. */
  function tamanhoTexto(bytes) {
    if (!bytes) return '';
    var mb = bytes / 1048576;
    if (mb >= 1) return (mb >= 10 ? Math.round(mb) : Math.round(mb * 10) / 10) + ' MB';
    return Math.max(1, Math.round(bytes / 1024)) + ' KB';
  }

  /* Dois formatos de saida:
     - QUADRADA (padrao), com recorte centralizado: a foto de perfil, 512. O
       corte e do cliente e o servidor so encolhe (APLICACAO-EVENTOS P6-8 e
       R-20). A foto so aparece em caixa quadrada com `cover`, entao o recorte
       so tira do arquivo o que a tela ja cortava.
     - LIVRE (`livre`), com a proporcao da foto e o MAIOR lado ate `lado`: a capa
       (R27), que sobe inteira, 2048, e e recortada no servidor pelo retangulo
       que o editor escolheu. Ate a R27 a capa saia num quadrado de 1200 e a
       faixa cortava de novo: uma imagem 3:1 perdia dois tercos da largura.
     Sem AUMENTAR imagem pequena: esticar uma foto de 300px gastaria banda para
     piorar a imagem. Num drawImage so, direto no tamanho final: o iPhone tem
     teto de area e de memoria total de canvas, e por isso o canvas e zerado
     depois do toBlob (sem isso a memoria so volta quando o coletor quiser).
     Tudo dentro do executor: um drawImage que lance (ou um getContext nulo) vira
     rejeicao, e nao um onload que estoura e deixa a tela presa no envio. */
  function pintar(fonte, w, h, lado, livre) {
    return new Promise(function (ok, nao) {
      /* a origem e a imagem inteira (livre) ou o quadrado central dela */
      var s = Math.min(w, h) || 1, sw = livre ? w : s, sh = livre ? h : s;
      var k = Math.min(1, lado / Math.max(sw, sh));
      var c = document.createElement('canvas');
      c.width = Math.max(1, Math.round(sw * k));
      c.height = Math.max(1, Math.round(sh * k));
      c.getContext('2d').drawImage(fonte, (w - sw) / 2, (h - sh) / 2, sw, sh, 0, 0, c.width, c.height);
      c.toBlob(function (b) { c.width = c.height = 0; if (b) ok(b); else nao(); }, 'image/jpeg', QUALIDADE);
    });
  }

  /* O caminho do <img> aplica a orientacao do EXIF sozinho nos navegadores
     atuais (image-orientation: from-image e o padrao). */
  function viaImg(arquivo, lado, livre) {
    return new Promise(function (ok, nao) {
      var u = URL.createObjectURL(arquivo);
      var im = new Image();
      im.onload = function () {
        URL.revokeObjectURL(u);
        pintar(im, im.naturalWidth, im.naturalHeight, lado, livre).then(ok, nao);
      };
      im.onerror = function () { URL.revokeObjectURL(u); nao(); };
      im.src = u;
    });
  }

  /* `imageOrientation: 'from-image'` e o que respeita o EXIF do celular: o
     canvas apaga os metadados no re-encode, entao sem isso a foto tirada em pe
     chegaria deitada ao servidor e nao haveria mais EXIF para o sharp girar.
     O <img> e o segundo caminho em QUALQUER falha do primeiro: sem a funcao
     (o chamado lanca e cai no catch), lancando na hora, ou com a Promise
     REJEITADA, que e o que faz o motor que tem createImageBitmap mas nao conhece
     o valor 'from-image' (enum invalido em funcao que devolve Promise rejeita,
     nao lanca). Sem isso um JPG valido levava "formato nao abre". So quando os
     dois falham e que `enviar` pinta o erro de tipo. */
  function reduzir(arquivo, lado, livre) {
    var p;
    try { p = W.createImageBitmap(arquivo, { imageOrientation: 'from-image' }); } catch (e) {}
    if (!p || !p.then) return viaImg(arquivo, lado, livre);
    return p.then(function (bm) {
      return pintar(bm, bm.width, bm.height, lado, livre).then(function (b) {
        try { bm.close(); } catch (e) {}
        return b;
      });
    }).then(null, function () { return viaImg(arquivo, lado, livre); });
  }

  /* A previa sai do blob JA reduzido, e nao do arquivo original: e exatamente o
     que vai subir, e um HEIC que o navegador nao abre nunca vira <img> quebrada
     na tela - ele cai no erro de tipo antes de chegar aqui. */
  function mostrarPrevia(blob, fn) {
    if (!fn) return;
    var u = URL.createObjectURL(blob);
    if (ultimaPrevia) URL.revokeObjectURL(ultimaPrevia);
    ultimaPrevia = u;
    fn(u);
  }

  /* O envio, separado da reducao: o editor de recorte chama este mesmo `subir`
     com o blob que ele mostrou e `extra` = { recorte: "x,y,w,h" }. `arq` e o
     arquivo ESCOLHIDO, so para o erro de tamanho dizer o numero que a pessoa
     reconhece no Finder ou na galeria, e nao o do blob reduzido.
     O timeout de 120 s garante que o envio SEMPRE termina (o ontimeout cai no
     erro de rede): um "Cancelar" com a conexao pendurada se paga desfazendo o
     que subiu, e sem fim o desfazer nunca chegaria. */
  function subir(blob, extra, o, arq) {
    return new Promise(function (fim) {
      var x = new XMLHttpRequest();
      x.open('POST', o.url);
      x.timeout = 120000;
      /* O header custom e o que forca preflight: um POST multipart sem ele e
         "simple request" e seria disparavel de qualquer site. O servidor
         recusa o upload sem ele (acesso.ts). */
      x.setRequestHeader('X-MW-Upload', '1');
      if (x.upload && o.andamento) {
        x.upload.onprogress = function (ev) {
          if (ev.lengthComputable && ev.total) o.andamento(ev.loaded / ev.total);
        };
      }
      x.onload = function () {
        var j = corpoJson(x.responseText);
        if (x.status >= 200 && x.status < 300 && j.ok) { if (o.ok) o.ok(j); }
        else if (o.erro) {
          var e = erroDe(x.status, j);
          e.tam = tamanhoTexto((arq || blob).size);
          o.erro(e);
        }
        fim();
      };
      x.onerror = x.ontimeout = x.onabort = function () {
        if (o.erro) o.erro({ c: 'rede' });
        fim();
      };
      var fd = new FormData();
      /* Nome do campo fixado pelo servidor (`lerArquivo`). O Content-Type NAO
         e setado a mao: quem escreve o boundary do multipart e o FormData. */
      fd.append('arquivo', blob, 'imagem.jpg');
      for (var k in extra) fd.append(k, extra[k]);
      x.send(fd);
    });
  }

  function enviar(f, o) {
    reduzir(f, o.lado).then(function (blob) {
      mostrarPrevia(blob, o.previa);
      subir(blob, null, o, f);
    }, function () {
      if (o.erro) o.erro({ c: 'tipo' });
    });
  }

  /* O DELETE dispensa o X-MW-Upload (nao tem corpo, ja e preflightado) e sobe
     pelo mesmo App Proxy assinado do resto da pagina. */
  function remover(url) {
    return fetch(url, { method: 'DELETE' }).then(function (r) {
      return r.text().then(function (t) {
        var j = corpoJson(t);
        if (!r.ok || !j.ok) throw erroDe(r.status, j);
        return j;
      });
    });
  }

  /* O editor carrega sob demanda, UMA vez: a promessa fica guardada, e volta a
     nulo quando falha, para a proxima tentativa buscar de novo em vez de
     devolver a mesma falha para sempre. */
  function carregar(src) {
    return editor || (editor = new Promise(function (ok, nao) {
      var s = document.createElement('script');
      s.src = src;
      s.onload = function () { if (W.MWRecorte) ok(W.MWRecorte); else nao(); };
      s.onerror = nao;
      document.head.appendChild(s);
    }).then(null, function (e) { editor = null; throw e; }));
  }
  var api = { reduzir: reduzir, subir: subir, remover: remover };

  /* `ligar` mora aqui, e nao nas duas paginas, porque o `value = ''` e o detalhe
     que some numa revisao: sem ele, escolher o MESMO arquivo de novo (depois de
     um erro, que e justo quando a pessoa tenta de novo) nao dispara `change`. */
  function ligar(entrada, o) {
    if (!entrada) return;
    var js = entrada.getAttribute('data-recorte') && entrada.getAttribute('data-recorte-js');
    entrada._mw = o;
    /* o clique que abre a escolha de arquivo ja comeca a baixar o editor:
       quando a pessoa volta com a foto, ele costuma estar pronto */
    if (js) entrada.addEventListener('click', function () { carregar(js).then(null, function () {}); });
    entrada.addEventListener('change', function () {
      var f = entrada.files && entrada.files[0];
      entrada.value = '';
      if (!f) return;
      if (js) {
        return carregar(js).then(function (R) { R.enviar(f, o, api); }, function () {
          if (o.erro) o.erro({ c: 'rede' });
        });
      }
      if (o.antes) o.antes();
      enviar(f, o);
    });
  }

  /* "Editar capa": a mesma entrada e as mesmas opcoes, com o editor no modo de
     ajuste (a capa salva, sem enviar a imagem de novo) */
  function editar(entrada) {
    return carregar(entrada.getAttribute('data-recorte-js')).then(function (R) { R.editar(entrada._mw, api); });
  }

  W.MWMidia = { ligar: ligar, remover: remover, editar: editar };
})();
